import { useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/free-mode';
import { ISlide } from '@features/productPreview/typescript';
import { WindowType } from '@typescript/index';
import styles from '@features/productPreview/sections/Gallery/FullMode/GallerySliderFullMode.styles';
import { FULL_MODE_IMAGE_ACTIVE_HEIGHT, FULL_MODE_IMAGE_HEIGHT } from '@features/productPreview/const';
import { uiState } from '@features/productPreview/mobX/state/context';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
type PropsType = {
  selectedSlider: ISlide;
  slides: ISlide[];
};
const GallerySliderFullMode = ({
  selectedSlider,
  slides
}: PropsType) => {
  const {
    windowDimensions,
    setSelectedSlider,
    hideSlider,
    productName
  } = uiState;
  const {
    t
  } = useTranslation('common');
  const galleryAlt = [productName, t('arrangement.single')].filter(Boolean).join(' ');
  const windowWidth = windowDimensions.width;
  const windowHeight = windowDimensions.height;
  const swiperInstance = useRef<SwiperClass>(null);
  const [activeSlide, setActiveSlide] = useState(selectedSlider);
  const [arrowsVisible, setArrowsVisible] = useState<boolean>(false);
  const containerHeight = 150; // slider cotainer height
  const availableHeight = windowHeight - containerHeight;
  const onNextClick = () => {
    if (!swiperInstance) return;
    swiperInstance.current?.slideNext();
  };
  const onPrevClick = () => {
    if (!swiperInstance) return;
    swiperInstance.current?.slidePrev();
  };
  const renderArrows = (swiper: SwiperClass) => {
    const fullImgContainerPadding = 300;
    if (windowWidth - fullImgContainerPadding < swiper.width) {
      setArrowsVisible(true);
    } else {
      setArrowsVisible(false);
    }
  };
  const renderImage = useMemo(() => <ResponsiveImage alt={galleryAlt} image={activeSlide.image[WindowType.desktop]} width={availableHeight} height={availableHeight} objectFit="contain" objectPosition="center" dataCy="GalleryImage" />, [activeSlide, availableHeight, galleryAlt]);
  const changeSlide = (slider: ISlide) => {
    setSelectedSlider(slider);
    setActiveSlide(slider);
  };
  return <div sx={styles.fullModeContainer} data-cy="GallerySliderFullMode">
      <div sx={styles.fullModeContentContainer}>
        <Button variant="iconOnly" cy="CloseButton" addSx={styles.closeButton} onClick={hideSlider}>
          <Icon icon="Close2" size={['16px', '16px', '21px', '21px', '24px']} />
        </Button>
        <div sx={styles.fullModeImg}>{renderImage}</div>

        <div sx={styles.sliderContainer}>
          <Swiper spaceBetween={24} slidesPerView="auto" pagination={{
          clickable: true
        }} onSwiper={swiper => {
          swiperInstance.current = swiper;
        }} onAfterInit={renderArrows} onResize={renderArrows} data-cy="Swiper">
            {slides.map((slider, index) => <SwiperSlide key={slider.id}>
                <div sx={styles.sliderImg} onClick={() => changeSlide(slider)} onKeyDown={e => e.key === 'Enter' && changeSlide(slider)} role="button" tabIndex={0}>
                  <ResponsiveImage alt={`${galleryAlt} ${index + 1}`} image={slider.image[WindowType.desktop]} width={activeSlide.id === slider.id ? FULL_MODE_IMAGE_ACTIVE_HEIGHT : FULL_MODE_IMAGE_HEIGHT} height={activeSlide.id === slider.id ? FULL_MODE_IMAGE_ACTIVE_HEIGHT : FULL_MODE_IMAGE_HEIGHT} objectFit="cover" objectPosition="center" />
                </div>
              </SwiperSlide>)}
          </Swiper>
        </div>
        {arrowsVisible && <div>
            <Button variant="iconOnly" onClick={onPrevClick} addSx={styles.sliderArrowButton('left')} cy="ArrowLeft">
              <Icon icon="ChevronLeft" size="46px" ariaHidden />
            </Button>
            <Button variant="iconOnly" onClick={onNextClick} addSx={styles.sliderArrowButton('right')} cy="ArrowRight">
              <Icon icon="ChevronRight" size="46px" ariaHidden />
            </Button>
          </div>}
      </div>
    </div>;
};
export default observer(GallerySliderFullMode);